// ============================================================================
// HR Inbox — dashboard widget for HR / executive / superuser roles.
//
// Mirrors finance-widget.tsx in structure. Surfaces a tight list of HR-
// actionable counts as deep links into the relevant module. Antonela's
// daily-driver view sits here.
// ============================================================================

import { Link } from 'react-router-dom';
import { ArrowUpRight, AlertTriangle, FileText, Users } from 'lucide-react';
import { WidgetCard, WidgetSkeleton } from '../widget-card';
import { useHrInbox, type HrInboxCounts } from './use-inbox-counts';

interface Props {
  enabled: boolean;
  myEid: string | null;
}

interface Row {
  key: keyof HrInboxCounts;
  label: string;
  to: string;
  icon: typeof Users;
}

const ROWS: Row[] = [
  {
    key: 'open_hiring_requests',
    label: 'Open hiring requests (Open + Interview + On Hold)',
    to: '/hiring?scope=all&stage=Open,Interview,On_Hold',
    icon: Users,
  },
  {
    key: 'subs_no_valid_coi',
    label: 'Working subs with no valid COI',
    // The card IS the doorway to the list. Same predicate, same count.
    to: '/people/subcontractors?tab=insurance',
    icon: AlertTriangle,
  },
  {
    key: 'open_hr_tasks',
    label: 'Open HR tasks (assigned to me)',
    to: '/tasks?category=HR&assignee=me',
    icon: FileText,
  },
];

export function HrWidget({ enabled, myEid }: Props) {
  const { data, isLoading, error } = useHrInbox(enabled, myEid);

  if (!enabled) return null;

  return (
    <WidgetCard
      title="HR inbox"
      subtitle="What's stacked up for HR."
      minHeight={240}
    >
      {error && <p className="text-xs text-st-danger-fg">Couldn't load: {error}</p>}
      {!error && isLoading && <WidgetSkeleton lines={3} />}
      {!error && !isLoading && data && (
        <ul className="-mx-2 divide-y divide-tcs-border/60">
          {ROWS.map((row) => {
            const value = data[row.key];
            const Icon = row.icon;
            const display = value === null ? '—' : value.toLocaleString('en-US');
            return (
              <li key={row.key}>
                <Link
                  to={row.to}
                  className="group flex items-center justify-between gap-3 px-2 py-2 text-sm text-tcs-text hover:bg-tcs-surface"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <Icon
                      size={14}
                      className="shrink-0 text-violet-600"
                      aria-hidden
                    />
                    <span className="truncate">{row.label}</span>
                  </span>
                  <span className="flex items-center gap-1 text-tcs-text-muted">
                    <span className="tabular-nums font-medium text-tcs-text">
                      {display}
                    </span>
                    <ArrowUpRight
                      size={12}
                      className="text-tcs-text-faint group-hover:text-tcs-text-secondary"
                      aria-hidden
                    />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetCard>
  );
}
